import AuthApi from './authApi'

export const iframe = typeof document !== 'undefined' ? document.createElement('iframe') : null
let store, messageHandlers, timer
let authSuccess, authFailure, returnUserStateResponse, returnSoftCheckResponse, returnLoggedInResponse

export const init = config => {
  if (typeof window !== 'undefined') {
    store = config

    messageHandlers = {
      LOGIN: receiveAuth,
      SIGNUP: receiveAuth,
      GET_USER: userStateResponse,
      SOFT_CHECK: softCheckResponse,
      LOGGED_IN_CHECK: loggedInResponse
    }

    iframe.src = `${store.frontend}?origin=${window.location.origin}`
    iframe.style = 'display: none'

    insertDOMElements()

    window.addEventListener('message', receiveMessage, false)
  }
}

const insertDOMElements = () => {
  document.body.appendChild(iframe)
}

const iframeLoaded = () =>
  new Promise(resolve => {
    if (iframe.contentWindow) return resolve()
    iframe.addEventListener('load', resolve)
  })

const receiveMessage = async msg => {
  if (msg.origin !== store.frontend) return
  await iframeLoaded()
  clearTimeout(timer)
  const handler = messageHandlers[msg.data.type]
  if (handler) return handler(msg.data.payload, msg.data.error)
}

const sendMessage = async msg => {
  await iframeLoaded()
  iframe.contentWindow.postMessage(msg, '*')
}

export const login = (email, password) =>
  new Promise(async (resolve, reject) => {
    const body = { email, password }
    try {
      const { data } = await AuthApi.login(body)
      sendMessage({ type: 'LOGIN', payload: data })
      resolve(data)
    } catch (e) {
      reject(e.response)
    }
  })

export const signup = user =>
  new Promise(async (resolve, reject) => {
    try {
      const { data } = await AuthApi.signup(user)
      sendMessage({ type: 'SIGNUP', payload: data })
      resolve(data)
    } catch (e) {
      reject(e.response)
    }
  })

export const updateUser = async (user, token) => {
  const res = await AuthApi.updateUser(user, token)
  const { user: newUser } = res.data
  sendMessage({ type: 'SIGNUP', payload: { user: newUser, token: token } })
  return newUser
}

export const updateCache = async ({ user, token }) => {
  sendMessage({ type: 'SIGNUP', payload: { user, token } })
}

export const setSoftVerifyUser = user => {
  sendMessage({ type: 'SET_SOFT_VERIFIED_USER', payload: { user } })
}

const receiveAuth = (data, err) => {
  if (err) return authFailure(err)

  if (data.token) {
    if (authSuccess) authSuccess(data)
  } else {
    if (authFailure) authFailure(data)
  }
}

export const getUserState = () =>
  new Promise(resolve => {
    sendMessage({ type: 'GET_USER' })
    returnUserStateResponse = resolve
  })

export const getUser = token =>
  new Promise(async (resolve, reject) => {
    try {
      const { data } = await AuthApi.me(token)
      sendMessage({ type: 'LOGIN', payload: { ...data, token } })
      resolve({ ...data, token })
    } catch (e) {
      reject(e.response)
    }
  })

export const userStateResponse = data => {
  returnUserStateResponse({ user: JSON.parse(data.user), token: data.token })
}

export const getSoftVerifiedUser = () =>
  new Promise(resolve => {
    sendMessage({ type: 'GET_SOFT_VERIFIED_USER' })
    returnSoftCheckResponse = resolve
  })

export const softCheckResponse = data => {
  returnSoftCheckResponse({
    softVerifiedUser:
      data.softVerifiedUser && data.softVerifiedUser !== 'undefined' && JSON.parse(data.softVerifiedUser)
  })
}

export const checkLoggedIn = () =>
  new Promise(resolve => {
    sendMessage({ type: 'CHECK_LOGGED_IN' })
    returnLoggedInResponse = resolve
  })

const loggedInResponse = data => {
  returnLoggedInResponse(data)
}

export const forgotPassword = body =>
  new Promise(async (resolve, reject) => {
    try {
      const { data } = await AuthApi.forgotPassword(body)
      resolve(data)
    } catch (e) {
      reject(e.response)
    }
  })

export const resetPassword = body =>
  new Promise(async (resolve, reject) => {
    try {
      const { data } = await AuthApi.resetPassword(body)
      resolve(data)
    } catch (e) {
      reject(e.response)
    }
  })

export const screenEmail = body =>
  new Promise(async (resolve, reject) => {
    try {
      const { data } = await AuthApi.screenEmail(body)
      resolve(data)
    } catch (e) {
      reject(e.response)
    }
  })

export const logout = () =>
  new Promise(() => {
    sendMessage({ type: 'LOGOUT' })
  })

export const getJumioAuthToken = body =>
  new Promise(async (resolve, reject) => {
    try {
      const { data } = await AuthApi.getJumioAuthToken(body)
      resolve(data)
    } catch (e) {
      reject(e.response)
    }
  })

export const verifyEmail = body =>
  new Promise(async (resolve, reject) => {
    try {
      const { data } = await AuthApi.verifyEmail(body)
      resolve(data)
    } catch (e) {
      reject(e.response)
    }
  })

export const sendVypeVerificationEmail = body =>
  new Promise(async (resolve, reject) => {
    try {
      const { data } = await AuthApi.sendVypeVerificationEmail(body)
      resolve(data)
    } catch (e) {
      console.log(e)
      reject(e.response)
    }
  })

export const cancelOrder = body =>
  new Promise(async (resolve, reject) => {
    try {
      const { data } = await AuthApi.cancelOrder(body)
      resolve(data)
    } catch (e) {
      console.log(e)
      reject(e.response)
    }
  })

export const getPriceBreakdown = ({ params, token }) =>
  new Promise(async (resolve, reject) => {
    try {
      const { data } = await AuthApi.getPriceBreakdown({ params, token })
      resolve(data)
    } catch (e) {
      console.log(e)
      reject(e.response)
    }
  })

export const getShippingMethods = ({ params, token }) =>
  new Promise(async (resolve, reject) => {
    try {
      const { data } = await AuthApi.getShippingMethods({ params, token })
      resolve(data)
    } catch (e) {
      console.log(e)
      reject(e.response)
    }
  })

export const completeCheckout = ({ body, token }) =>
  new Promise(async (resolve, reject) => {
    try {
      const { data } = await AuthApi.completeCheckout({ body, token })
      resolve(data)
    } catch (e) {
      console.log(e)
      reject(e.response)
    }
  })

export const getCanadaPostOffices = zip =>
  new Promise(async (resolve, reject) => {
    try {
      const { data } = await AuthApi.getCanadaPostOffices(zip)
      resolve(data)
    } catch (e) {
      console.log(e)
      reject(e.response)
    }
  })

export const getRetailers = body =>
  new Promise(async (resolve, reject) => {
    try {
      const { data } = await AuthApi.getRetailers(body)
      resolve(data)
    } catch (e) {
      console.log(e)
      reject(e.response)
    }
  })

export const deletePaymentMethod = body =>
  new Promise(async (resolve, reject) => {
    try {
      const { data } = await AuthApi.deletePaymentMethod(body)
      resolve(data)
    } catch (e) {
      console.log(e)
      reject(e.response)
    }
  })

export const addPaymentMethod = body =>
  new Promise(async (resolve, reject) => {
    try {
      const { data } = await AuthApi.addPaymentMethod(body)
      resolve(data)
    } catch (e) {
      console.log(e)
      reject(e.response)
    }
  })

export const cancelSubscription = body =>
  new Promise(async (resolve, reject) => {
    try {
      const { data } = await AuthApi.cancelSubscription(body)
      resolve(data)
    } catch (e) {
      console.log(e)
      reject(e.response)
    }
  })

export const editSubscription = body =>
  new Promise(async (resolve, reject) => {
    try {
      const { data } = await AuthApi.editSubscription(body)
      resolve(data)
    } catch (e) {
      console.log(e)
      reject(e.response)
    }
  })

export const getOrder = async body => {
  const { data } = await AuthApi.getOrder(body)
  return data
}

export const submitCalloutApplication = async body => {
  const { data } = await AuthApi.submitCalloutApplication(body)
  return data
}

export const getProducts = async () => {
  const { data } = await AuthApi.getProducts()
  return data
}

export const createBoostPlanOrder = async body => {
  const { data } = await AuthApi.createBoostPlanOrder(body)
  return data
}

export const claimTransitionEpod = async (body, token) => {
  const { data } = await AuthApi.claimTransitionEpod(body, token)
  return data
}

export const validateEmail = async body => {
  const { data } = await AuthApi.validateEmail(body)
  return data
}

export const getSaasquatchRewards = token =>
  new Promise(async (resolve, reject) => {
    try {
      const { data } = await AuthApi.getSaasquatchRewards(token)
      resolve(data)
    } catch (e) {
      reject(e.response)
    }
  })

export const getSaasquatchRewardsBalance = token =>
  new Promise(async (resolve, reject) => {
    try {
      const { data } = await AuthApi.getSaasquatchRewardsBalance(token)
      resolve(data)
    } catch (e) {
      reject(e.response)
    }
  })

export const submitCalloutVotes = async (body, token) => {
  const { data } = await AuthApi.submitCalloutVotes(body, token)
  return data
}

export const getSubscriptionPriceBreakdown = async ({ body, token }) => {
  const { data } = await AuthApi.getSubscriptionPriceBreakdown({ body, token })
  return data
}

export const checkThirdPartyCodeEligibility = async email => {
  const { data } = await AuthApi.checkThirdPartyCodeEligibility(email)
  return data
}

export const redeemThirdPartyCode = async body => {
  const { data } = await AuthApi.redeemThirdPartyCode(body)
  return data
}
